import {
  Button,
  Checkbox,
  RadioButton,
  RadioButtonGroup,
  TextArea,
  Tooltip,
} from '@carbon/react';
import { Calculation, Information, Pause } from '@carbon/react/icons';
import { useStore } from '@tanstack/react-store';
import { useState } from 'react';

import { CalculationIconTrigger } from 'components/calculation-icon-trigger';
import {
  NumberField,
  numberToPlainString,
  TanstackFieldWrapper,
} from 'components/form-fields-2';
import { LazyUnitInterestCard } from 'components/lazy-unit-interest-card/lazy-unit-interest-card';
import { Link } from 'components/link';
import { UatNriMatrixCard } from 'components/uat-nri-matrix-card/uat-nri-matrix-card';
import { format8 } from 'utils/number';

import type {
  ConfirmationGroup,
  ConfirmationSource,
  SlotInterestRow,
  SlotSubtract,
  StreamCode,
  TractDraft,
} from '../types';
import {
  canSubmitTract,
  certifiedStreamsFor,
  CUSTOM_INTEREST_DECIMALS,
  emptyTractDraft,
  hasCustomInterestPrecision,
  STATUS_DISPLAY,
  STREAM_LABELS,
  streamsLabel,
  submittableStreams,
  tractKey,
  tractPrimarySource,
  type TractPrimarySource,
} from '../utils';
import { useWorkspaceConfirmation } from '../workspace-confirmation-context';
import { TractHoldSection } from './tract-hold-section';
import styles from './tract-confirm-section.module.scss';

interface TractConfirmSectionProps {
  row: SlotInterestRow;
  /** The tract this section certifies, or null for the no-subtract
   *  pseudo-tract (slot-level pathway). */
  subtract: SlotSubtract | null;
  /** Streams certified for this tract during the current panel session —
   *  merged with the BE state so the section reflects the session's submits
   *  before the table refetch lands. */
  sessionCertifiedStreams: StreamCode[];
  /** Submits this tract's draft. The section disables itself while pending. */
  onConfirm: (subtract: SlotSubtract | null, draft: TractDraft) => void;
  submitting: boolean;
  /** When set, this card represents one already-certified evidence-source act
   *  (a confirmation group) — read-only until Edit, scoped to the group's
   *  streams. When null/absent, the card is the editor for the uncertified
   *  remainder (free + held streams). */
  group?: ConfirmationGroup | null;
}

type TractStatusKind = 'on_hold' | 'certified' | 'in_progress' | 'not_started';

/**
 * The per-tract status pill in the card header — On Hold (red) overrides
 * everything, else Certified / In Progress / Not Started mirroring the slot
 * comparison table's status icons.
 *
 * @param {object} props - Component props.
 * @param {TractStatusKind} props.status - The resolved status.
 * @return {JSX.Element} The status indicator.
 */
const TractStatus = ({ status }: { status: TractStatusKind }): JSX.Element => {
  const { Icon, label } = STATUS_DISPLAY[status];
  const className = {
    on_hold: styles.statusOnHold,
    certified: styles.statusCertified,
    in_progress: styles.statusInProgress,
    not_started: styles.statusNotStarted,
  }[status];
  return (
    <span className={styles.status}>
      <Icon size={16} className={className} />
      {label}
    </span>
  );
};

/**
 * One value cell of the source-comparison table. AM cells carry the live
 * calculation + UAT-matrix popovers (keyed off the subtract's `nriCalculationId`,
 * reusing the subtracts-table pattern); DO cells are plain.
 *
 * @param {object} props - Component props.
 * @param {number | null} props.value - The NRI or WI value.
 * @param {'NRI' | 'WI'} props.calculationType - Which value this cell shows.
 * @param {number | null} props.nriCalculationId - The calc id, or null for no popovers.
 * @return {JSX.Element} The cell content.
 */
const SourceValueCell = ({
  value,
  calculationType,
  nriCalculationId,
}: {
  value: number | null;
  calculationType: 'NRI' | 'WI';
  nriCalculationId: number | null;
}): JSX.Element => (
  <span className={styles.valueCell}>
    <span>{format8(value, '—')}</span>
    {nriCalculationId !== null && (
      <>
        <LazyUnitInterestCard
          nriCalculationId={nriCalculationId}
          unitInterestValue={value}
          calculationType={calculationType}
          renderTrigger={(active, toggle) => (
            <CalculationIconTrigger
              definition="Show Calculation"
              active={active}
              onClick={toggle}
            >
              <span className={styles.icon}>
                <Calculation aria-hidden="true" role="img" />
              </span>
            </CalculationIconTrigger>
          )}
        />
        <UatNriMatrixCard
          nriCalculationId={nriCalculationId}
          calculationType={calculationType}
        />
      </>
    )}
  </span>
);

/**
 * AM / DO source-comparison table — the selected source row is rail-highlighted.
 * AM values come from the tract's live per-subtract NriCalculation (the
 * pseudo-tract falls back to the slot rollup); DO from the tract's DO snapshot.
 *
 * @param {object} props - Component props.
 * @return {JSX.Element} The table.
 */
const SourceTable = ({
  source,
  subtract,
  selectedSource,
}: {
  source: TractPrimarySource;
  subtract: SlotSubtract | null;
  selectedSource: ConfirmationSource | null;
}): JSX.Element => {
  const nriCalculationId = subtract?.nriCalculationId ?? null;
  const doNri = subtract?.doNri ?? null;
  const doWi = subtract?.doWi ?? null;

  return (
    <table className={styles.sourceTable}>
      <thead>
        <tr>
          <th>Source</th>
          <th>Net revenue interest</th>
          <th>Working interest</th>
        </tr>
      </thead>
      <tbody>
        {/* Primary source row — "AM" when Allocation Manager is selectable, else
            "GIS" for an estimate that can't be certified to AM. */}
        <tr
          className={
            source.amSelectable && selectedSource === 'allocation_manager'
              ? styles.selectedRow
              : ''
          }
        >
          <td>{source.label}</td>
          <td>
            <SourceValueCell
              value={source.nri}
              calculationType="NRI"
              nriCalculationId={nriCalculationId}
            />
          </td>
          <td>
            <SourceValueCell
              value={source.wi}
              calculationType="WI"
              nriCalculationId={nriCalculationId}
            />
          </td>
        </tr>
        <tr
          className={
            selectedSource === 'division_order' ? styles.selectedRow : ''
          }
        >
          <td>DO</td>
          <td>{format8(doNri, '—')}</td>
          <td>{format8(doWi, '—')}</td>
        </tr>
      </tbody>
    </table>
  );
};

/**
 * One tract's certification card inside the Confirm Interest panel (partial
 * confirmations), redesigned to the Select Evidence Source mock: a status
 * header, the evidence-source radios, the AM/DO source-comparison table, the
 * Streams checkboxes, and an Edit / On Hold footer.
 *
 * A touched tract shows a read-only summary (disabled radios + source table)
 * until Edit re-opens the editor; a Not Started tract opens in the editor. The
 * editor adds the Custom NRI/WI inputs + supporting note and the Confirm
 * action. Form state binds to `tracts.${tractKey}` on the shared provider-owned
 * form, so sibling tracts' drafts survive this tract's submit/refetch.
 *
 * @param {TractConfirmSectionProps} props - Component props.
 * @return {JSX.Element} The tract card.
 */
export const TractConfirmSection = ({
  row,
  subtract,
  sessionCertifiedStreams,
  onConfirm,
  submitting,
  group = null,
}: TractConfirmSectionProps): JSX.Element => {
  const { form } = useWorkspaceConfirmation();
  const isGroupCard = group !== null;
  // Per-UAT grain (ONCALL-668): the draft key is per (subtract, UAT) segment, so
  // two UAT segments of the same subtract own independent drafts / notes.
  const baseKey = tractKey(
    subtract?.subtractId ?? null,
    subtract?.uatId ?? null
  );
  // Each card owns its own draft. A certified act's editor is keyed by its
  // source + streams (a subtract can have two Custom acts at different values,
  // so source alone collides) so editing it never clobbers the remainder editor
  // (base key) or a sibling act's draft.
  const key = isGroupCard
    ? `${baseKey}::${group.source}::${group.streams.join('-')}`
    : baseKey;
  const draft = useStore(form.store, (state) => state.values.tracts[key]) as
    TractDraft | undefined;

  // Local UI flags — reset on panel close/re-open (a fresh mount).
  const [editing, setEditing] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);

  // Streams surfaced as red "held" tags and used to pre-select the editor —
  // always the persisted hold. Holds are narrowed by RELEASING streams, not by
  // editing other streams, so the red hold card stays put while you edit the
  // rest of the tract.
  const displayedHeldStreams = subtract?.hold?.heldStreams ?? [];

  // Raw strings typed into the Custom NRI/WI inputs (precision validation needs
  // the digits as typed). null = untouched this mount.
  const [customText, setCustomText] = useState<{
    nri: string | null;
    wi: string | null;
  }>({ nri: null, wi: null });

  // Streams already covered by a certified act card (one per source). The
  // remainder card owns whatever's left. The no-subtract pseudo-tract has no
  // groups, so it keeps the whole-tract behaviour.
  const hasGroups = (subtract?.confirmationGroups?.length ?? 0) > 0;
  const groupCovered = new Set<StreamCode>(
    (subtract?.confirmationGroups ?? []).flatMap((g) => g.streams)
  );

  // The streams this card owns. A certified act → its streams. A remainder card
  // of a grouped subtract → the streams no act covers (hiding ones certified
  // this session pre-refetch). Otherwise (pseudo-tract / not-yet-grouped) → the
  // whole tract, exactly as before.
  const scopeStreams = isGroupCard
    ? row.streams.filter((stream) => group.streams.includes(stream))
    : hasGroups
      ? row.streams.filter(
          (stream) =>
            !groupCovered.has(stream) &&
            !sessionCertifiedStreams.includes(stream)
        )
      : row.streams;

  // Certified streams in scope: all of a group card's; none on a grouped
  // subtract's remainder (uncertified by construction); the real certified set
  // for the whole-tract case.
  const certifiedSet = new Set<StreamCode>(
    isGroupCard
      ? group.streams
      : hasGroups
        ? []
        : [...certifiedStreamsFor(row, subtract), ...sessionCertifiedStreams]
  );
  const certified = scopeStreams.filter((stream) => certifiedSet.has(stream));
  const fullyCertified =
    certified.length > 0 && certified.length === scopeStreams.length;
  const tractConfirmation = subtract?.confirmation ?? row.confirmation ?? null;
  const onHold = subtract?.onHold ?? false;
  // Streams currently on hold for this tract — locked in the editor (greyed,
  // never confirmed) until the hold is released for them.
  const heldStreams = subtract?.hold?.heldStreams ?? [];
  // The primary (non-Custom) source for this tract — AM when Allocation Manager
  // is selectable, else a GIS estimate. Drives the AM radio + the source table's
  // row label/value.
  const primarySource = tractPrimarySource(row, subtract);
  // A certified act's read-only NRI/WI: the entered value, except AM which has
  // no entered value and shows the live per-tract figure.
  const groupNri = group
    ? group.source === 'allocation_manager'
      ? primarySource.nri
      : group.confirmedNri
    : null;
  const groupWi = group
    ? group.source === 'allocation_manager'
      ? primarySource.wi
      : group.confirmedWi
    : null;

  // A group card is a certified act; a grouped subtract's remainder is the
  // uncertified editor (On Hold if held); the whole-tract case keeps the full
  // status ladder so the pseudo-tract still reads Certified / In Progress.
  const status: TractStatusKind = isGroupCard
    ? 'certified'
    : onHold
      ? 'on_hold'
      : hasGroups
        ? 'not_started'
        : fullyCertified
          ? 'certified'
          : certified.length > 0
            ? 'in_progress'
            : 'not_started';

  const title = subtract
    ? (subtract.tractName ?? subtract.subtractName)
    : row.wellName;
  const tractHref = subtract?.tractHref ?? null;
  // Mirror the table's identity (Subtract · UAT) so two UAT segments of the
  // same subtract are distinguishable on their cards (ONCALL-668). UAT shows an
  // em-dash fallback when the segment has no UAT, matching the accordion table.
  const subtitleParts = subtract
    ? [subtract.subtractName, `UAT: ${subtract.uatName ?? '—'}`].filter(Boolean)
    : [];
  const subtitle = subtitleParts.length > 0 ? subtitleParts.join(' · ') : null;

  // Certify-by-default: an un-certified tract (Not Started / In Progress) opens
  // with its editor live — source radios, streams, and the Confirm button —
  // exactly like the original flow. Already-certified or on-hold cards stay
  // read-only until Edit re-opens the editor.
  const showEditor =
    editing || (status !== 'certified' && status !== 'on_hold');
  // The committed (read-only) source: a group card shows its act's source, the
  // remainder card the tract-level confirmation.
  const committedSource = isGroupCard
    ? group.source
    : (tractConfirmation?.source ?? null);
  const selectedSource = (
    showEditor ? (draft?.source ?? null) : committedSource
  ) as ConfirmationSource | null;

  const startEdit = (): void => {
    // A group card re-opens exactly its act — same streams + source + entered
    // values — so Edit re-certifies (or re-sources) just those streams. A held
    // remainder card starts from the red-tag streams; otherwise from the
    // certified set (empty for the remainder).
    const seeded: TractDraft = isGroupCard
      ? {
          ...emptyTractDraft(group.streams),
          source: group.source,
          nri: group.confirmedNri,
          wi: group.confirmedWi,
          // Reopening a certified act prefills this card's supporting-note
          // textarea with that group's saved note (INS-10203).
          note: group.note ?? '',
        }
      : {
          ...emptyTractDraft(
            displayedHeldStreams.length > 0 ? displayedHeldStreams : certified
          ),
          source: (tractConfirmation?.source ??
            null) as ConfirmationSource | null,
          nri: subtract?.confirmation?.confirmedNri ?? null,
          wi: subtract?.confirmation?.confirmedWi ?? null,
          note: tractConfirmation?.note ?? '',
        };
    // `as never`: TanStack's typed paths can't resolve dynamic Record keys.
    form.setFieldValue(`tracts.${key}`, seeded as never);
    setCustomText({ nri: null, wi: null });
    setEditing(true);
  };

  const toggleStream = (stream: StreamCode, checked: boolean): void => {
    if (!draft) return;
    const next = checked
      ? [...draft.selectedStreams, stream]
      : draft.selectedStreams.filter((s) => s !== stream);
    form.setFieldValue(
      `tracts.${key}.selectedStreams`,
      // `as never`: dynamic Record path. Canonical order, within this card's
      // scope.
      scopeStreams.filter((s) => next.includes(s)) as never
    );
  };

  // Effective Custom input text: typed this mount, else the draft value
  // rendered the way NumberField renders it (seeded edits).
  const customTextFor = (kind: 'nri' | 'wi'): string => {
    const typed = customText[kind];
    if (typed !== null) return typed;
    const seeded = draft?.[kind] ?? null;
    return seeded === null ? '' : numberToPlainString(seeded);
  };
  const nriText = customTextFor('nri');
  const wiText = customTextFor('wi');
  const nriPrecisionShort =
    nriText !== '' && !hasCustomInterestPrecision(nriText);
  const wiPrecisionShort = wiText !== '' && !hasCustomInterestPrecision(wiText);
  const customPrecisionOk =
    draft?.source !== 'user_entered' ||
    (hasCustomInterestPrecision(nriText) && !wiPrecisionShort);
  const precisionHelperText = `Enter all ${CUSTOM_INTEREST_DECIMALS} decimal places — zeros count (e.g. 0.10000000)`;
  const precisionInvalidText = `Needs at least ${CUSTOM_INTEREST_DECIMALS} digits after the decimal — pad with zeros (e.g. 0.10000000)`;

  // In edit mode every stream in scope is re-selectable (a group card thus
  // re-certifies its act); otherwise only the uncertified streams in scope.
  const selectableStreams = editing
    ? scopeStreams
    : scopeStreams.filter((stream) => !certifiedSet.has(stream));

  // The streams a submit would actually carry — deliberately the SAME call the
  // Certify handler makes below, rather than a new predicate, so the button's
  // disabled state and the handler cannot disagree. That is what makes the
  // handler's `if (confirmStreams.length === 0) return;` provably unreachable
  // instead of merely unlikely (INS-11671 / ic-08). The early return STAYS as a
  // backstop: it costs nothing, and removing it would let a future edit to the
  // disabled list reopen a silent no-op with nothing to catch it.
  const submittable = submittableStreams(
    draft?.selectedStreams ?? [],
    selectableStreams,
    heldStreams
  );
  // WHY a submit would carry nothing, so the disabled button can say it. The
  // governing rule (INS-11669 catalogue, section 1) is that an operation which
  // silently does nothing is worse than one that fails: this condition must
  // never disable the control without a reason shown AND associated. So the
  // reason is derived for EVERY cause of an empty submittable set, not just
  // holds.
  //
  // Two causes, matching the two filters inside `submittableStreams`. A held
  // stream is dropped for being held; a stream outside `selectableStreams` is
  // dropped for being certified already (the handler's own comment below names
  // it as such). The sets are disjoint by construction, and their union is the
  // whole selection whenever `submittable` is empty and something is selected
  // — every selected stream must have failed one filter or the other — so the
  // wording below always has at least one stream to name.
  //
  // WORDING: these name the stream and its status, NOT the selection. A held
  // stream sits in the draft but renders UNCHECKED and disabled (:684), so on a
  // partial-hold card the person sees zero ticked boxes the instant Edit is
  // pressed — `startEdit` seeds `emptyTractDraft(displayedHeldStreams)` (:389).
  // Saying "every stream selected here" was true of the draft and false to the
  // eye on exactly that card, which is the most common blocked state.
  //
  // The COMBINED wording is defensive and no test pins it: no reachable state
  // was found where both causes bind at once. A hold forces the card to
  // `on_hold`, which means the editor is only open with `editing` true, and
  // that makes every stream in scope selectable again — so the certified cause
  // cannot bind alongside a hold. It is kept rather than dropped because that
  // argument rests on the status ladder and the scope rules agreeing, and a
  // change to either would make a silent, unexplained disable the failure mode.
  const selectedStreams = draft?.selectedStreams ?? [];
  const blockedByHold = selectedStreams.filter((stream) =>
    heldStreams.includes(stream)
  );
  const blockedByCertified = selectedStreams.filter(
    (stream) =>
      !heldStreams.includes(stream) && !selectableStreams.includes(stream)
  );
  // Scoped to `showEditor` because the sentence exists to explain the Certify
  // control, which only exists when the editor is open; a read-only held card
  // already reads "Needs Further Review" and carries the hold thread. Scoped to
  // a non-empty selection because an empty one is `canSubmitTract`'s business
  // and says so through the streams themselves.
  // Only tell the person to pick another stream when one actually exists.
  // On an ALL-held card there is none, and "Select an available stream" would
  // be the same species of untrue-to-the-screen instruction this reword exists
  // to remove. `submittable` being empty already means no available stream is
  // currently selected, so any unheld selectable stream is one they could pick.
  const pickAnother = selectableStreams.some(
    (stream) => !heldStreams.includes(stream)
  )
    ? ' Select an available stream.'
    : '';
  const blockedReasonId = `tract-${key}-blocked-reason`;
  const blockedReason =
    !showEditor || selectedStreams.length === 0 || submittable.length > 0
      ? null
      : blockedByHold.length > 0 && blockedByCertified.length > 0
        ? `Cannot certify ${streamsLabel(
            blockedByHold
          )} (on review hold) or ${streamsLabel(
            blockedByCertified
          )} (already certified).${pickAnother}`
        : blockedByHold.length > 0
          ? `Cannot certify ${streamsLabel(
              blockedByHold
            )} — on review hold.${pickAnother}`
          : `Cannot certify ${streamsLabel(
              blockedByCertified
            )} — already certified.${pickAnother}`;

  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <div className={styles.titleGroup}>
          {tractHref !== null ? (
            <Link className={styles.title} to={tractHref} openInNewTab>
              {title}
            </Link>
          ) : (
            <span className={styles.title}>{title}</span>
          )}
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          {certified.length > 0 && (
            <p className={styles.subtitle}>
              Certified: {streamsLabel(certified)}
            </p>
          )}
          {/* A grouped subtract's remainder card shares its title and subtitle
              with the act cards beside it, so name the streams it still owns. */}
          {!isGroupCard && hasGroups && scopeStreams.length > 0 && (
            <p className={styles.subtitle}>
              Not certified: {streamsLabel(scopeStreams)}
            </p>
          )}
        </div>
        <TractStatus status={status} />
      </header>

      <RadioButtonGroup
        legendText="Evidence source"
        name={`source-${key}`}
        orientation="horizontal"
        valueSelected={selectedSource ?? ''}
        // Read-only by default; Edit enables the radios so the source can be
        // changed before Update.
        disabled={!showEditor || !draft}
        onChange={(value) =>
          // `as never`: dynamic Record path.
          form.setFieldValue(
            `tracts.${key}.source`,
            value as ConfirmationSource as never
          )
        }
      >
        <RadioButton
          id={`source-${key}-am`}
          value="allocation_manager"
          labelText="AM"
          // No Allocation Manager on this slot — the value is a GIS estimate, so
          // AM can't be the evidence source (use Custom).
          disabled={!primarySource.amSelectable}
        />
        <RadioButton
          id={`source-${key}-do`}
          value="division_order"
          labelText="DO"
          // No live Division Order source yet — DO stays disabled until DO data
          // is wired up.
          disabled
        />
        <RadioButton
          id={`source-${key}-custom`}
          value="user_entered"
          labelText="Custom"
        />
      </RadioButtonGroup>

      <SourceTable
        source={primarySource}
        subtract={subtract}
        selectedSource={selectedSource}
      />

      {/* Certified act, read-only: surface the certified NRI/WI (Custom shows the
          entered value; AM/GIS the live figure). */}
      {isGroupCard && !showEditor && (
        <dl className={styles.certifiedValues}>
          <div className={styles.certifiedField}>
            <dt className={styles.certifiedLabel}>Certified NRI</dt>
            <dd className={styles.certifiedValue}>
              {groupNri === null ? '—' : format8(groupNri)}
            </dd>
          </div>
          <div className={styles.certifiedField}>
            <dt className={styles.certifiedLabel}>Certified WI</dt>
            <dd className={styles.certifiedValue}>
              {groupWi === null ? '—' : format8(groupWi)}
            </dd>
          </div>
          {/* Surface the act's saved supporting note read-only, so reopening a
              certified tract shows its note without entering the editor. */}
          {(group?.note ?? '').trim().length > 0 && (
            <div className={styles.certifiedNoteField}>
              <dt className={styles.certifiedLabel}>Supporting note</dt>
              <dd className={styles.certifiedValue}>{group?.note}</dd>
            </div>
          )}
        </dl>
      )}

      {showEditor && draft?.source === 'user_entered' && (
        <div className={styles.customInputs}>
          <div className={styles.customField}>
            <label className={styles.customLabel} htmlFor={`tract-${key}-nri`}>
              Custom NRI
            </label>
            <form.Field name={`tracts.${key}.nri`}>
              {(field) => (
                <TanstackFieldWrapper<number | null> field={field}>
                  {(props) => (
                    <NumberField
                      id={`tract-${key}-nri`}
                      placeholder="0.00000000"
                      thousandSeparator={false}
                      decimalScale={CUSTOM_INTEREST_DECIMALS}
                      helperText={precisionHelperText}
                      {...props}
                      invalid={props.invalid || nriPrecisionShort}
                      invalidText={
                        nriPrecisionShort
                          ? precisionInvalidText
                          : props.invalidText
                      }
                      onTextChange={(text) =>
                        setCustomText((prev) => ({ ...prev, nri: text }))
                      }
                    />
                  )}
                </TanstackFieldWrapper>
              )}
            </form.Field>
          </div>
          <div className={styles.customField}>
            <label className={styles.customLabel} htmlFor={`tract-${key}-wi`}>
              Custom WI
            </label>
            <form.Field name={`tracts.${key}.wi`}>
              {(field) => (
                <TanstackFieldWrapper<number | null> field={field}>
                  {(props) => (
                    <NumberField
                      id={`tract-${key}-wi`}
                      placeholder="0.00000000"
                      thousandSeparator={false}
                      decimalScale={CUSTOM_INTEREST_DECIMALS}
                      helperText={precisionHelperText}
                      {...props}
                      invalid={props.invalid || wiPrecisionShort}
                      invalidText={
                        wiPrecisionShort
                          ? precisionInvalidText
                          : props.invalidText
                      }
                      onTextChange={(text) =>
                        setCustomText((prev) => ({ ...prev, wi: text }))
                      }
                    />
                  )}
                </TanstackFieldWrapper>
              )}
            </form.Field>
          </div>
        </div>
      )}

      <fieldset className={styles.streams}>
        <legend className={styles.streamsLegend}>Streams</legend>
        {scopeStreams.map((stream) => {
          // A stream is locked when it's not selectable (already certified) or
          // on hold. In the editor a locked stream renders UNCHECKED (it never
          // rides along in a submit); the read-only view mirrors the red held
          // tags / certified set instead.
          const streamDisabled =
            !showEditor ||
            !selectableStreams.includes(stream) ||
            heldStreams.includes(stream);
          const streamChecked = showEditor
            ? !streamDisabled &&
              (draft?.selectedStreams.includes(stream) ?? false)
            : // A certified act card always shows its own streams checked — a
              // hold on the subtract's OTHER streams (shown on the remainder card)
              // must not blank them out. Only the remainder card reflects the hold.
              isGroupCard
              ? certifiedSet.has(stream)
              : onHold
                ? displayedHeldStreams.includes(stream)
                : certifiedSet.has(stream);
          return (
            <Checkbox
              key={stream}
              id={`stream-${key}-${stream}`}
              labelText={STREAM_LABELS[stream]}
              checked={streamChecked}
              disabled={streamDisabled}
              onChange={(_event, { checked }) => toggleStream(stream, checked)}
            />
          );
        })}
      </fieldset>

      {/* Per-card supporting note, bound to this card's draft. A Custom
          (user_entered) certify requires a note (BE rule); reopening a certified
          act prefills it from `group.note` (INS-10203). */}
      {showEditor && (
        <form.Field name={`tracts.${key}.note`}>
          {(field) => (
            <TextArea
              id={`tract-${key}-note`}
              labelText={
                <span className={styles.noteLabel}>
                  Supporting note
                  <Tooltip
                    label="A supporting note is required to certify Custom interest."
                    align="top"
                    autoAlign
                  >
                    <button
                      type="button"
                      className={styles.noteInfo}
                      aria-label="When is a supporting note required?"
                    >
                      <Information />
                    </button>
                  </Tooltip>
                </span>
              }
              rows={3}
              placeholder="Document the reasoning behind this decision…"
              value={(field.state.value as string | undefined) ?? ''}
              onChange={(event) =>
                // `as never`: dynamic Record path.
                field.handleChange(event.target.value as never)
              }
            />
          )}
        </form.Field>
      )}

      <footer className={styles.actions}>
        {showEditor ? (
          <Button
            size="sm"
            // Point a screen reader at the reason when there is one, so the
            // disabled control is explained rather than simply dead.
            aria-describedby={
              blockedReason !== null ? blockedReasonId : undefined
            }
            disabled={
              submitting ||
              !draft ||
              !canSubmitTract(row, subtract, draft) ||
              !customPrecisionOk ||
              // Nothing this card would submit survives the filters, so a
              // press would confirm nothing. Mirrors the handler's own
              // submittableStreams call exactly (INS-11671 / ic-08).
              //
              // The argument does not rest on holds: `submittable` IS the set
              // the handler would send, so an empty one means the press
              // certifies nothing, whatever emptied it. Nor does it duplicate
              // `canSubmitTract` on an unheld card — that predicate reads the
              // draft's source and selection only, never `selectableStreams`,
              // so it stays TRUE while the selection has gone unsubmittable.
              // Pinned by 'offers no usable certify control, and says why,
              // once the draft still selects a stream certified this session'
              // (__test__/tract-confirm-section.test.tsx), which reaches that
              // state with no hold anywhere on the card.
              submittable.length === 0 ||
              // Custom (user_entered) certification requires a supporting note
              // (BE rule) — keep Certify disabled until this card's note is
              // filled.
              (draft?.source === 'user_entered' &&
                (draft.note ?? '').trim().length === 0)
            }
            onClick={() => {
              if (!draft) return;
              // Only confirm streams whose checkbox is actually ENABLED: drop
              // held streams (locked) AND already-certified streams (disabled,
              // not in selectableStreams) so a disabled-but-checked box never
              // rides along in the submit.
              const confirmStreams = submittableStreams(
                draft.selectedStreams,
                selectableStreams,
                heldStreams
              );
              if (confirmStreams.length === 0) return;
              onConfirm(subtract, {
                ...draft,
                selectedStreams: confirmStreams,
              });
              // Return the card to its read-only state — the row refetch then
              // reflects the new certification.
              setEditing(false);
            }}
          >
            {editing ? 'Update' : 'Certify'}
          </Button>
        ) : (
          <Button size="sm" onClick={startEdit}>
            Edit
          </Button>
        )}
        {/* Why Certify is unavailable, beside the control it explains. Visible
            text rather than the tooltip pattern used on the supporting-note
            label above: that pattern suits a conditional rule attached to a
            field, whereas this condition is actively blocking the person right
            now and must not have to be discovered by hovering. */}
        {blockedReason !== null && (
          <p className={styles.blockedReason} id={blockedReasonId}>
            {blockedReason}
          </p>
        )}
        {/* On Hold opens the reason modal; hidden once held (the held block
            below shows the thread + Release instead). Holds live on the
            remainder card, not the certified-act cards. */}
        {subtract !== null && !subtract.hold && !isGroupCard && (
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Pause}
            // Same lock as Certify: `submitting` here already carries the
            // panel's stale-row state, and placing a hold off a stale row
            // writes against values the user can no longer see.
            disabled={submitting}
            onClick={() => setHoldOpen(true)}
          >
            Mark for Review
          </Button>
        )}
        {editing && (
          <Button
            kind="ghost"
            size="sm"
            disabled={submitting}
            onClick={() => setEditing(false)}
          >
            Cancel
          </Button>
        )}
      </footer>

      {subtract !== null && !isGroupCard && (
        <TractHoldSection
          row={row}
          subtract={subtract}
          open={holdOpen}
          onClose={() => setHoldOpen(false)}
          // What "Put on hold" actually holds: the streams checked in the
          // editor (so the user can hold a subset). Falls back to the card's
          // scope.
          selectedStreams={draft?.selectedStreams ?? scopeStreams}
          // The red held-stream tags always show the persisted hold, so they
          // stay put while the user edits the tract's other streams.
          previewStreams={displayedHeldStreams}
          // Release / Add Comment / the put-on-hold modal mutate the slot the
          // panel is showing, so they honour the panel's lock too. Without
          // this they stay live while the stale banner claims editing is
          // locked — the exact path INS-11586 is about (clearing the last
          // review).
          locked={submitting}
        />
      )}
    </section>
  );
};
